/**
 * Sammelt Bedienschritte und schickt sie gebündelt (#44).
 *
 * Marlene am 26.08.2026: „dass sie wüsste welche Wege wir so gehen immer und
 * obs einfacher ginge." Die Vorprüfung zeigte, dass das Activity-Log das nicht
 * hergibt — es führt Ergebnisse von Systemvorgängen, keine Bedienschritte.
 *
 * 🔴 **Ohne Inhalte.** Gemeldet wird nur, WELCHER Schritt auf WELCHER Seite —
 * kein Betreff, kein Kundenname, keine Mail-ID. Für „welchen Weg gehen wir
 * ständig" reicht das vollständig, und was nicht erfasst wird, kann auch nicht
 * auslaufen. Der Server verwirft zusätzlich alles, was nicht wie ein Bezeichner
 * aussieht.
 *
 * 🔑 Gebündelt statt einzeln: ein Request je Klick wäre bei einem Büro, das den
 * ganzen Tag im Portal arbeitet, ein spürbarer Strom — und der Zweck ist die
 * Abfolge, nicht die Sofortmeldung.
 */

const SCHLUESSEL = "wege-sitzung"
/** Nach dieser Ruhezeit geht das Gesammelte raus. */
const WARTEZEIT = 5000
/** Spätestens bei so vielen Schritten, damit ein langer Arbeitsgang nicht
 *  alles bis zum Schluss aufhebt. */
const SPAETESTENS = 25

type Schritt = { schritt: string; seite: string }

let offen: Schritt[] = []
let timer: ReturnType<typeof setTimeout> | null = null

/** Eine Kennung je Browser-Sitzung — ohne sie ließe sich keine Abfolge lesen.
 *  Sie steckt im `sessionStorage`, gilt also für einen Tab und endet mit ihm. */
function sitzung(): string {
  try {
    let s = sessionStorage.getItem(SCHLUESSEL)
    if (!s) {
      s = Math.random().toString(36).slice(2) + Date.now().toString(36)
      sessionStorage.setItem(SCHLUESSEL, s)
    }
    return s
  } catch {
    // Privates Fenster o.ä. — dann eben eine flüchtige Kennung.
    return "ohne-speicher"
  }
}

/** Die Seite ohne Parameter und ohne IDs — `/emails?open=145193` wird
 *  `/emails`. Ein Pfad mit ID wäre wieder ein Inhalt. */
function seite(): string {
  try {
    return window.location.pathname.replace(/\/\d+(?=\/|$)/g, "").slice(0, 60) || "/"
  } catch {
    return "/"
  }
}

function senden() {
  if (timer) { clearTimeout(timer); timer = null }
  const schritte = offen
  offen = []
  if (!schritte.length) return
  const koerper = JSON.stringify({ sitzung: sitzung(), schritte })
  try {
    // `sendBeacon` überlebt das Schließen des Tabs — genau dann geht der letzte
    // Arbeitsgang sonst verloren, und der ist oft der interessante.
    if (navigator.sendBeacon) {
      navigator.sendBeacon("/api/v1/wege", new Blob([koerper], { type: "application/json" }))
      return
    }
  } catch { /* fällt unten auf fetch zurück */ }
  fetch("/api/v1/wege", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: koerper,
    keepalive: true,
  }).catch(() => {
    // 🔴 Die Erfassung ist eine Nebensache. Ein Fehler hier darf nie sichtbar
    // werden und schon gar nichts blockieren.
  })
}

/**
 * Meldet einen Bedienschritt.
 *
 * `name` ist ein Bezeichner in Kleinbuchstaben, z.B. `anhang_an_projekt` oder
 * `dialog_aufgabe_geoeffnet`. Alles andere verwirft der Server still.
 */
export function wegSchritt(name: string) {
  const sauber = (name || "").trim().toLowerCase()
  if (!/^[a-z0-9_]{3,60}$/.test(sauber)) return
  offen.push({ schritt: sauber, seite: seite() })
  if (offen.length >= SPAETESTENS) { senden(); return }
  if (timer) clearTimeout(timer)
  timer = setTimeout(senden, WARTEZEIT)
}

/** Beim Verlassen der Seite nachreichen, was noch aussteht. */
export function wegeAnmelden() {
  if (typeof window === "undefined") return
  window.addEventListener("pagehide", senden)
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") senden()
  })
}

/** Nur für Tests: den Puffer leeren. */
export function _wegePuffer() {
  return offen
}
